import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getClientAnalytics, analyzeClientMarket, getCachedAnalysis, narrativeToBlocks, rangeFor,
  getClientOutreach,
  type ReportBlock, type ReportNarrative, type ClientOutreach,
} from '../../lib/refonteApi';
import { usePortalScope } from './portal/usePortalScope';
import { ReportBlocks, Blk } from '../../components/refonte/ReportView';
import { Skeleton, SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

// En-tête de section (lecture logique et groupée pour le client). id = ancre pour les onglets.
const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="mb-6 scroll-mt-24">
    <h2 className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2.5 border-b border-line pb-1.5">{title}</h2>
    {children}
  </section>
);

// Onglets d'ancrage — accès direct aux sections (barre collante sous l'en-tête).
// Analyse IA marché masquée en attendant le déploiement propre de generate-report-narrative
// (edge function non déployée → le bouton plantait). Roadmap : déployer + garde anti-hallucination.
const SHOW_AI_MARKET = false;
const SECTION_TABS: { id: string; label: string }[] = [
  { id: 'sec-volume', label: 'Volume' },
  { id: 'sec-resultats', label: 'Résultats' },
  { id: 'sec-activite', label: 'Activité' },
  { id: 'sec-detail', label: 'Détail' },
  { id: 'sec-ciblage', label: 'Ciblage' },
  ...(SHOW_AI_MARKET ? [{ id: 'sec-ia', label: 'Analyse IA' }] : []),
];
const SectionTabs: React.FC = () => (
  <div className="sticky top-0 z-10 -mx-8 px-8 py-2 mb-4 bg-cream/90 backdrop-blur border-b border-line flex flex-wrap gap-1.5">
    {SECTION_TABS.map(t => (
      <a key={t.id} href={`#${t.id}`}
        className="text-[11.5px] font-semibold px-2.5 py-1 rounded-md border border-line-2 text-dim hover:border-ink hover:text-ink transition-colors">{t.label}</a>
    ))}
  </div>
);
const Grid2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">{children}</div>
);

// Portail client — PERFORMANCE & MARCHÉ. Le client choisit la plage (1M/3M/tout),
// voit ses chiffres en direct, et lance l'analyse IA de son marché (mise en cache,
// rafraîchissable). Chiffres = déterministes (live) ; analyse = IA à la demande.
const ClientPerformancePage: React.FC = () => {
  const scope = usePortalScope();
  const { clientId, clientName } = scope;

  const now = new Date();
  const lastCompleted = new Date(now.getFullYear(), now.getMonth() - 1, 1); // le mois en cours n'est pas fini
  const [selYear, setSelYear] = useState(lastCompleted.getFullYear());
  const [selMonth, setSelMonth] = useState(lastCompleted.getMonth() + 1);
  const [blocks, setBlocks] = useState<ReportBlock[]>([]);
  const [outreach, setOutreach] = useState<ClientOutreach | null>(null); // mail/LinkedIn (surtout GTM)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [narrative, setNarrative] = useState<ReportNarrative | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState<string | null>(null);

  // Toujours un mois civil précis → clé de cache YYYY-MM.
  const resolve = useCallback(() => {
    const { fromISO, toISO } = rangeFor('month', selYear, selMonth);
    return { fromISO, toISO, key: `${selYear}-${String(selMonth).padStart(2, '0')}` };
  }, [selYear, selMonth]);

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setErr(null); setAiErr(null);
    const { fromISO, toISO, key } = resolve();
    try {
      const [analytics, cached, outr] = await Promise.all([
        getClientAnalytics(clientId, fromISO, toISO),
        getCachedAnalysis(clientId, key).catch(() => null),
        getClientOutreach(clientId, fromISO, toISO).catch(() => null),
      ]);
      setBlocks(analytics.blocks);
      setOutreach(outr);
      setNarrative(cached?.narrative ?? null);
      setGeneratedAt(cached?.generated_at ?? null);
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [clientId, resolve]);
  useEffect(() => { load(); }, [load]);

  const runAi = async () => {
    if (!clientId) return;
    setAiBusy(true); setAiErr(null);
    const { fromISO, toISO, key } = resolve();
    try {
      const res = await analyzeClientMarket(clientId, fromISO, toISO, key);
      setNarrative(res.narrative); setGeneratedAt(res.generated_at);
    } catch (e: any) { setAiErr(e.message); }
    setAiBusy(false);
  };

  const cQuery = clientId ? `?c=${clientId}` : '';
  const iaBlocks = narrative ? narrativeToBlocks(narrative) : [];
  const byId: Record<string, ReportBlock> = Object.fromEntries(blocks.map(b => [b.id, b]));

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Performance & marché</h1>
        <div className="flex items-center gap-3">
          <Link to={`/app/portail${cQuery}`} className="text-[12px] font-semibold text-dim hover:text-ink">← Tableau de bord</Link>
          {(scope.isInternal || scope.options.length > 1) && (
            <label className="flex items-center gap-2 text-[12px] text-dim">
              {scope.isInternal && <span className="font-mono text-[9px] uppercase tracking-widest text-faint">Client</span>}
              <select value={clientId ?? ''} onChange={e => scope.setClientId(e.target.value || null)}
                className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] max-w-[220px]">
                {!clientId && <option value="">— choisir —</option>}
                {scope.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          )}
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">
        Vos chiffres en direct + l'analyse de votre marché par l'IA
        {clientName && <span className="text-faint"> · {clientName}</span>}
      </p>

      {(err || scope.error) && <EmptyState tone="error" title="Chargement impossible" hint={err || scope.error || ''} className="mb-4" />}

      {scope.resolving ? <Skeleton className="h-[60vh] rounded-card" /> : !clientId ? (
        <EmptyState title="Aucun client rattaché" hint="Votre compte n'est associé à aucun client." />
      ) : (
        <>
          {/* Sélecteur de mois (les calculs portent toujours sur ce mois précis) */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="font-mono text-[9px] uppercase tracking-widest text-faint mr-1">Mois</span>
            <button onClick={() => { const d = new Date(selYear, selMonth - 2, 1); setSelYear(d.getFullYear()); setSelMonth(d.getMonth() + 1); }}
              aria-label="Mois précédent" className="w-8 h-8 rounded-md border border-line-2 hover:border-ink">‹</button>
            <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] capitalize">
              {MOIS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={selYear} onChange={e => setSelYear(Number(e.target.value))} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]">
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => { const d = new Date(selYear, selMonth, 1); if (d <= lastCompleted) { setSelYear(d.getFullYear()); setSelMonth(d.getMonth() + 1); } }}
              aria-label="Mois suivant" className="w-8 h-8 rounded-md border border-line-2 hover:border-ink">›</button>
          </div>

          {/* Chiffres déterministes (live), organisés par sections logiques */}
          {loading ? <SkeletonRows count={6} /> : blocks.length === 0 ? (
            <EmptyState title="Aucune donnée sur ce mois" hint="Choisis un autre mois avec les flèches ci-dessus." />
          ) : (
            <>
              <SectionTabs />
              <Section id="sec-volume" title="Volume du mois"><Blk block={byId.kpisVolume} /></Section>
              {outreach?.hasAny && (
                <Section id="sec-digital" title="Mail & LinkedIn du mois">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {([['mail', '✉️ Mail', outreach.mail], ['linkedin', '💼 LinkedIn', outreach.linkedin]] as const).map(([k, label, st]) => (
                      (st.sent > 0 || st.replies > 0) ? (
                        <div key={k} className="rounded-card border border-line bg-panel-2 p-4">
                          <div className="text-[13px] font-semibold mb-2">{label}</div>
                          <div className="grid grid-cols-2 gap-2 font-mono text-[12px]">
                            <div><div className="text-faint text-[9px] uppercase tracking-widest">{k === 'mail' ? 'Mails envoyés' : 'Messages envoyés'}</div><b className="text-[17px]">{st.sent}</b></div>
                            <div><div className="text-faint text-[9px] uppercase tracking-widest">Réponses</div><b className="text-[17px]">{st.replies}</b></div>
                            <div><div className="text-faint text-[9px] uppercase tracking-widest">Positives</div><b className="text-[17px] text-hook-rose">{st.positives}</b></div>
                            <div><div className="text-faint text-[9px] uppercase tracking-widest">Taux de réponse</div><b className="text-[17px]">{st.sent > 0 ? Math.round((st.replies / st.sent) * 100) : 0}%</b></div>
                          </div>
                        </div>
                      ) : null
                    ))}
                  </div>
                  <p className="font-mono text-[9px] text-faint mt-2">Volumes réellement envoyés pour vous, mesurés depuis nos outils d'envoi (Instantly / HeyReach). Détail des réponses dans « Registre des campagnes ».</p>
                </Section>
              )}
              <Section id="sec-resultats" title="Résultats · rendez-vous">
                <div className="space-y-4">
                  <Blk block={byId.kpisResults} />
                  <Grid2><Blk block={byId.statutChart} /><Blk block={byId.conv} /></Grid2>
                </div>
              </Section>
              <Section id="sec-activite" title="Activité & qualité des conversations">
                <div className="space-y-4">
                  <Grid2><Blk block={byId.evo} /><Blk block={byId.dist} /></Grid2>
                  <Blk block={byId.kpisQuality} />
                </div>
              </Section>
              <Section id="sec-detail" title="Détail de l'activité">
                <div className="space-y-4">
                  <Blk block={byId.semaine} />
                  {/* Performance PAR SDR = donnée interne (noms + productivité) — jamais côté client (audit UX 09/10 #1) */}
                  {scope.isInternal && !scope.viewAsClient && <Blk block={byId.sdr} />}
                  <Blk block={byId.daily} />
                  <Blk block={byId.detail} />
                </div>
              </Section>
              <Section id="sec-ciblage" title="Votre cible"><Blk block={byId.icp} /></Section>
            </>
          )}

          {/* Analyse IA du marché */}
          {SHOW_AI_MARKET && (
          <div id="sec-ia" className="mt-6 scroll-mt-24">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-[16px] font-bold">Analyse de votre marché (IA)</h2>
              <div className="flex items-center gap-3">
                {generatedAt && <span className="font-mono text-[10px] text-faint">mis à jour le {fmtDate(generatedAt)}</span>}
                <button disabled={aiBusy} onClick={runAi} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-md bg-hook-rose text-white disabled:opacity-40">
                  {aiBusy ? 'Analyse en cours…' : narrative ? '↻ Ré-analyser' : '✨ Analyser mon marché'}
                </button>
              </div>
            </div>
            {aiErr && <div className="mb-3 text-[12px] text-st-bad bg-st-bad/10 rounded px-3 py-2">{aiErr}</div>}
            {aiBusy && !narrative ? <SkeletonRows count={4} /> : iaBlocks.length ? (
              <ReportBlocks blocks={iaBlocks} />
            ) : (
              <EmptyState title="Marché pas encore analysé" hint="Lancez l'analyse IA pour obtenir objections, concurrents détectés, signaux d'intérêt et recommandations sur la période." />
            )}
          </div>
          )}

          <p className="font-mono text-[10px] text-faint mt-6">Chiffres mesurés en direct par Hook.{SHOW_AI_MARKET ? ' Analyse IA à la demande, mise en cache et rafraîchissable.' : ''}</p>
        </>
      )}
    </div>
  );
};

export default ClientPerformancePage;
